import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Hotel, Users, Building, DollarSign, Plus, Pencil, UserPlus, 
  User, Mail, Phone, MapPin, Shield, Trash2, AlertTriangle, Building2, Settings, Percent,
  Layers, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import LogoUpload from '@/components/LogoUpload';
import DeleteEntityDialog from '@/components/DeleteEntityDialog';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';
import HotelAmenitiesAllocationDialog from '@/components/HotelAmenitiesAllocationDialog';

export default function SuperAdminDashboard({ user: userProp, isTab, tabId, tabData }) {
  const [analytics, setAnalytics] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignAdminOpen, setAssignAdminOpen] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [selectedHotelForAdmin, setSelectedHotelForAdmin] = useState(null);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [showDeleteHotelDialog, setShowDeleteHotelDialog] = useState(false);
  const [hotelToDelete, setHotelToDelete] = useState(null);
  const [taxSettingsOpen, setTaxSettingsOpen] = useState(false);
  const [selectedHotelForTax, setSelectedHotelForTax] = useState(null);
  const [taxSettings, setTaxSettings] = useState({
    room_tax: 12,
    room_service_food_tax: 5,
    room_service_tax: 18,
    restaurant_food_tax: 5,
    restaurant_beverages_tax: 18,
    other_services_tax: 18
  });
  // Room Type Assignment state
  const [roomTypeDialogOpen, setRoomTypeDialogOpen] = useState(false);
  const [selectedHotelForRoomTypes, setSelectedHotelForRoomTypes] = useState(null);
  const [globalRoomTypes, setGlobalRoomTypes] = useState([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);
  const [savingRoomTypes, setSavingRoomTypes] = useState(false);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  
  // Hotel Amenities Allocation state
  const [amenitiesDialogOpen, setAmenitiesDialogOpen] = useState(false);
  const [selectedHotelForAmenities, setSelectedHotelForAmenities] = useState(null);
  
  // Get user from prop or localStorage (for tab-based rendering)
  const user = userProp || (isTab ? JSON.parse(localStorage.getItem('user') || '{}') : JSON.parse(localStorage.getItem('user') || '{}'));
  const isCompanyAdmin = user?.role === 'company_admin';
  
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_id: '',
    address: '',
    city: '',
    state: '',
    country: '',
    logo_url: ''
  });
  const navigate = useNavigate();
  const { openTab } = useTabs();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Different API calls based on role
      if (isCompanyAdmin) {
        // Company admin uses company-specific endpoints
        const companyId = user?.company_ids?.[0] || user?.company_id;
        const [hotelsRes, usersRes, companiesRes] = await Promise.all([
          api.get(`/companies/${companyId}/hotels`),
          api.get(`/companies/${companyId}/users`),
          api.get('/companies')
        ]);
        setHotels(hotelsRes.data);
        setAllUsers(usersRes.data);
        // Filter users who can be hotel admins
        setAdminUsers(usersRes.data.filter(u => 
          ['hotel_admin', 'manager', 'company_admin'].includes(u.role)
        ));
        setCompanies(companiesRes.data || []);
        setAnalytics({
          total_hotels: hotelsRes.data.length,
          total_users: usersRes.data.length
        });
      } else {
        // Super admin uses super-admin endpoints
        const [analyticsRes, hotelsRes, usersRes, adminsRes, companiesRes] = await Promise.all([
          api.get('/super-admin/analytics'),
          api.get('/super-admin/hotels'),
          api.get('/super-admin/users'),
          api.get('/super-admin/users/admins'),
          api.get('/companies')
        ]);
        setAnalytics(analyticsRes.data);
        setHotels(hotelsRes.data);
        setAllUsers(usersRes.data);
        setAdminUsers(adminsRes.data);
        setCompanies(companiesRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (hotelId) => {
    try {
      await api.post(`/super-admin/hotels/${hotelId}/suspend`);
      toast.success('Hotel suspended');
      loadData();
    } catch (error) {
      toast.error('Failed to suspend hotel');
    }
  };

  const handleActivate = async (hotelId) => {
    try {
      await api.post(`/super-admin/hotels/${hotelId}/activate`);
      toast.success('Hotel activated');
      loadData();
    } catch (error) {
      toast.error('Failed to activate hotel');
    }
  };

  const handleDeleteClick = (hotel) => {
    setHotelToDelete(hotel);
    setShowDeleteHotelDialog(true);
  };

  const handleTaxSettingsClick = async (hotel) => {
    setSelectedHotelForTax(hotel);
    try {
      const response = await api.get(`/super-admin/hotels/${hotel.id}/tax-settings`);
      setTaxSettings(response.data);
    } catch (error) {
      // Use defaults if not set
      setTaxSettings({
        room_tax: 12,
        room_service_food_tax: 5,
        room_service_tax: 18,
        restaurant_food_tax: 5,
        restaurant_beverages_tax: 18,
        other_services_tax: 18
      });
    }
    setTaxSettingsOpen(true);
  };

  const handleSaveTaxSettings = async () => {
    if (!selectedHotelForTax) return;
    try {
      await api.put(`/super-admin/hotels/${selectedHotelForTax.id}/tax-settings`, taxSettings);
      toast.success('Tax settings saved successfully');
      setTaxSettingsOpen(false);
    } catch (error) {
      toast.error('Failed to save tax settings');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!hotelToDelete) return;
    
    try {
      await api.delete(`/super-admin/hotels/${hotelToDelete.id}`);
      toast.success('Hotel deleted successfully');
      setShowDeleteHotelDialog(false);
      setHotelToDelete(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete hotel');
    }
  };

  const handleEditClick = (hotel) => {
    setEditingHotel(hotel);
    setEditFormData({
      name: hotel.name || '',
      email: hotel.email || '',
      phone: hotel.phone || '',
      company_id: hotel.company_id || '',
      address: hotel.address || '',
      city: hotel.city || '',
      state: hotel.state || '',
      country: hotel.country || '',
      logo_url: hotel.logo_url || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/super-admin/hotels/${editingHotel.id}`, editFormData);
      toast.success('Hotel updated successfully');
      setEditDialogOpen(false);
      setEditingHotel(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update hotel');
    }
  };

  const handleAssignAdminClick = (hotel) => {
    setSelectedHotelForAdmin(hotel);
    setSelectedAdminId('');
    setAssignAdminOpen(true);
  };

  const handleAssignAdmin = async () => {
    if (!selectedAdminId || !selectedHotelForAdmin) {
      toast.error('Please select an admin');
      return;
    }
    try {
      await api.post(`/super-admin/hotels/${selectedHotelForAdmin.id}/assign-admin?admin_user_id=${selectedAdminId}`);
      toast.success('Admin assigned successfully');
      setAssignAdminOpen(false);
      setSelectedHotelForAdmin(null);
      setSelectedAdminId('');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign admin'));
    }
  };

  // Room Type Assignment functions
  const openRoomTypeDialog = async (hotel) => {
    setSelectedHotelForRoomTypes(hotel);
    setLoadingRoomTypes(true);
    setRoomTypeDialogOpen(true);
    
    try {
      // Fetch global room types
      const globalRes = await api.get('/global-room-types');
      setGlobalRoomTypes(globalRes.data);
      
      // Fetch currently assigned room types for this hotel
      const assignedRes = await api.get(`/hotels/${hotel.id}/assigned-room-types`);
      setSelectedRoomTypes(assignedRes.data.assigned_ids || []);
    } catch (error) {
      console.error('Failed to load room types:', error);
      toast.error('Failed to load room types');
    } finally {
      setLoadingRoomTypes(false);
    }
  };

  const handleRoomTypeToggle = (roomTypeId) => {
    setSelectedRoomTypes(prev => {
      if (prev.includes(roomTypeId)) {
        return prev.filter(id => id !== roomTypeId);
      } else {
        return [...prev, roomTypeId];
      }
    });
  };

  const handleSaveRoomTypes = async () => {
    if (!selectedHotelForRoomTypes) return;
    
    setSavingRoomTypes(true);
    try {
      await api.put(`/hotels/${selectedHotelForRoomTypes.id}/assigned-room-types`, {
        room_type_ids: selectedRoomTypes
      });
      toast.success(`Assigned ${selectedRoomTypes.length} room types to ${selectedHotelForRoomTypes.name}`);
      setRoomTypeDialogOpen(false);
      setSelectedHotelForRoomTypes(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save room types'));
    } finally {
      setSavingRoomTypes(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'trial': return 'bg-blue-100 text-blue-700';
      case 'suspended': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-700';
      case 'hotel_admin': return 'bg-blue-100 text-blue-700';
      case 'manager': return 'bg-green-100 text-green-700';
      case 'front_office': return 'bg-amber-100 text-amber-700';
      case 'restaurant_staff': return 'bg-pink-100 text-pink-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="super-admin-loading">Loading...</div>;
  }

  return (
    <div className="space-y-8" data-testid="super-admin-dashboard">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Platform Overview
          </h1>
          <p className="text-muted-foreground mt-2">Manage all hotels, users, and monitor platform performance</p>
        </div>
        <Button onClick={() => openTab({
          type: TAB_TYPES.PAGE_CREATE_HOTEL,
          id: `create-hotel-${Date.now()}`,
          title: 'Add New Hotel',
          data: {}
        })} data-testid="create-hotel-button">
          <Plus className="h-4 w-4 mr-2" />
          Add Hotel
        </Button>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card hover-lift" data-testid="stat-total-hotels">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-muted-foreground mb-1">
                  Total Hotels
                </p>
                <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                  {analytics?.total_hotels || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Hotel className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift" data-testid="stat-active-hotels">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-muted-foreground mb-1">
                  Active Hotels
                </p>
                <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                  {analytics?.active_hotels || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-50 flex items-center justify-center">
                <Building className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift" data-testid="stat-total-users">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-muted-foreground mb-1">
                  Total Users
                </p>
                <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                  {analytics?.total_users || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-purple-50 flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift" data-testid="stat-total-revenue">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-muted-foreground mb-1">
                  Total Revenue
                </p>
                <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                  ₹{analytics?.total_revenue?.toLocaleString() || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-amber-50 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Hotels and Users */}
      <Tabs defaultValue="hotels" className="space-y-4">
        <TabsList>
          <TabsTrigger value="hotels" className="flex items-center gap-2">
            <Hotel className="h-4 w-4" />
            Hotels ({hotels.length})
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            All Users ({allUsers.length})
          </TabsTrigger>
        </TabsList>

        {/* Hotels Tab */}
        <TabsContent value="hotels">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>All Hotels</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {hotels.map((hotel) => (
                  <Card key={hotel.id} className="hover-lift" data-testid={`hotel-card-${hotel.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Hotel className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{hotel.name}</h3>
                              <Badge className={getStatusColor(hotel.status)} data-testid={`hotel-status-${hotel.id}`}>
                                {hotel.status}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mt-3">
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Email</p>
                                <p className="font-medium">{hotel.email}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Location</p>
                                <p className="font-medium">{hotel.city}, {hotel.state}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Users</p>
                                <p className="font-medium">{hotel.user_count || 0}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Rooms</p>
                                <p className="font-medium">{hotel.room_count || 0}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Admins</p>
                                <div className="flex flex-wrap gap-1">
                                  {hotel.assigned_admins && hotel.assigned_admins.length > 0 ? (
                                    hotel.assigned_admins.map(admin => (
                                      <Badge key={admin.id} variant="outline" className="text-xs">
                                        {admin.name}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-muted-foreground text-xs">No admin</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                          {isCompanyAdmin && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedHotelForAmenities(hotel);
                                  setAmenitiesDialogOpen(true);
                                }}
                                data-testid={`hotel-amenities-btn-${hotel.id}`}
                                className="border-green-200 text-green-600 hover:bg-green-50"
                              >
                                <Layers className="h-3 w-3 mr-1" />
                                Amenities
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openRoomTypeDialog(hotel)}
                                data-testid={`assign-room-types-${hotel.id}`}
                                className="border-blue-200 text-blue-600 hover:bg-blue-50"
                              >
                                <Layers className="h-3 w-3 mr-1" />
                                Room Types
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTaxSettingsClick(hotel)}
                            data-testid={`tax-settings-${hotel.id}`}
                          >
                            <Percent className="h-3 w-3 mr-1" />
                            Tax Settings
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAssignAdminClick(hotel)}
                            data-testid={`assign-admin-${hotel.id}`}
                          >
                            <UserPlus className="h-3 w-3 mr-1" />
                            Assign Admin
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditClick(hotel)}
                            data-testid={`edit-hotel-${hotel.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openTab({
                              type: TAB_TYPES.PAGE_HOTEL_DETAILS,
                              id: `hotel-details-${hotel.id}`,
                              title: hotel.name,
                              data: { hotelId: hotel.id }
                            })}
                            data-testid={`view-hotel-${hotel.id}`}
                          >
                            View Details
                          </Button>
                          {hotel.status === 'active' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSuspend(hotel.id)}
                              data-testid={`suspend-hotel-${hotel.id}`}
                            >
                              Suspend
                            </Button>
                          ) : hotel.status === 'suspended' ? (
                            <Button
                              size="sm"
                              onClick={() => handleActivate(hotel.id)}
                              data-testid={`activate-hotel-${hotel.id}`}
                            >
                              Activate
                            </Button>
                          ) : hotel.status === 'trial' ? (
                            <Button
                              size="sm"
                              onClick={() => handleActivate(hotel.id)}
                              data-testid={`activate-trial-hotel-${hotel.id}`}
                            >
                              Activate
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteClick(hotel)}
                            data-testid={`delete-hotel-${hotel.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>All Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allUsers.map((user) => (
                  <Card key={user.id} className="hover-lift" data-testid={`user-card-${user.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold truncate">{user.name}</h4>
                            <Badge className={getRoleBadgeColor(user.role)}>
                              {user.role.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground truncate flex items-center gap-1 mt-1">
                            <Mail className="h-3 w-3" />
                            {user.email}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <Building className="h-3 w-3" />
                            {user.hotel_name || 'No Hotel'}
                          </p>
                          {user.assigned_hotel_names && user.assigned_hotel_names.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-muted-foreground mb-1">Also manages:</p>
                              <div className="flex flex-wrap gap-1">
                                {user.assigned_hotel_names.map((name, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <Badge className={user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Hotel Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md" data-testid="edit-hotel-dialog">
          <DialogHeader>
            <DialogTitle>Edit Hotel</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="flex justify-center">
              <LogoUpload
                currentLogo={editFormData.logo_url}
                onLogoChange={(url) => setEditFormData({ ...editFormData, logo_url: url })}
                entityType="hotel"
                entityId={editingHotel?.id}
                size="md"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">Hotel Name</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
                data-testid="edit-hotel-name"
              />
            </div>
            
            {/* Company Selection */}
            <div className="space-y-2">
              <Label htmlFor="edit-company">Company</Label>
              <Select 
                value={editFormData.company_id || "none"} 
                onValueChange={(value) => setEditFormData({ ...editFormData, company_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="edit-hotel-company">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Company</SelectItem>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-500" />
                        <span>{company.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
                data-testid="edit-hotel-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                data-testid="edit-hotel-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                value={editFormData.address}
                onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                data-testid="edit-hotel-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-city">City</Label>
                <Input
                  id="edit-city"
                  value={editFormData.city}
                  onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                  data-testid="edit-hotel-city"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-state">State</Label>
                <Input
                  id="edit-state"
                  value={editFormData.state}
                  onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                  data-testid="edit-hotel-state"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-country">Country</Label>
              <Input
                id="edit-country"
                value={editFormData.country}
                onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                data-testid="edit-hotel-country"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1" data-testid="save-hotel-edit">
                Save Changes
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Admin Dialog */}
      <Dialog open={assignAdminOpen} onOpenChange={setAssignAdminOpen}>
        <DialogContent className="max-w-md" data-testid="assign-admin-dialog">
          <DialogHeader>
            <DialogTitle>Assign Admin to {selectedHotelForAdmin?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Admin User</Label>
              <Select value={selectedAdminId} onValueChange={setSelectedAdminId}>
                <SelectTrigger data-testid="select-admin-dropdown">
                  <SelectValue placeholder="Select an admin user" />
                </SelectTrigger>
                <SelectContent>
                  {adminUsers.map(admin => (
                    <SelectItem key={admin.id} value={admin.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{admin.name}</span>
                        <span className="text-xs text-muted-foreground">{admin.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The selected admin will be able to manage this hotel along with their existing hotels.
              </p>
            </div>
            
            {selectedHotelForAdmin?.assigned_admins?.length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium mb-2">Currently Assigned:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedHotelForAdmin.assigned_admins.map(admin => (
                    <Badge key={admin.id} variant="outline">
                      {admin.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button 
                onClick={handleAssignAdmin} 
                className="flex-1"
                disabled={!selectedAdminId}
                data-testid="confirm-assign-admin"
              >
                Assign Admin
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAssignAdminOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Hotel Dialog */}
      <DeleteEntityDialog
        open={showDeleteHotelDialog}
        onOpenChange={setShowDeleteHotelDialog}
        entityType="hotel"
        entityId={hotelToDelete?.id}
        entityName={hotelToDelete?.name}
        onDeleted={() => {
          setHotelToDelete(null);
          loadData();
        }}
      />

      {/* Tax Settings Dialog */}
      <Dialog open={taxSettingsOpen} onOpenChange={setTaxSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-primary" />
              Tax Settings - {selectedHotelForTax?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Configure tax percentages for different service categories. These will be applied during billing.
            </p>
            
            {/* Room Tax */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Hotel className="h-4 w-4 text-blue-500" />
                Hotel Room Tax (%)
              </Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={taxSettings.room_tax}
                onChange={(e) => setTaxSettings({ ...taxSettings, room_tax: parseFloat(e.target.value) || 0 })}
                placeholder="12"
              />
            </div>
            
            {/* Room Service Food Tax */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <span className="h-4 w-4 text-orange-500">🍽️</span>
                Room Service Food Tax (%)
              </Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={taxSettings.room_service_food_tax}
                onChange={(e) => setTaxSettings({ ...taxSettings, room_service_food_tax: parseFloat(e.target.value) || 0 })}
                placeholder="5"
              />
            </div>
            
            {/* Room Service (Other) Tax */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <span className="h-4 w-4 text-purple-500">🛎️</span>
                Room Service (Other) Tax (%)
              </Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={taxSettings.room_service_tax}
                onChange={(e) => setTaxSettings({ ...taxSettings, room_service_tax: parseFloat(e.target.value) || 0 })}
                placeholder="18"
              />
            </div>
            
            {/* Restaurant Food Tax */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <span className="h-4 w-4 text-green-500">🍴</span>
                Restaurant Food Tax (%)
              </Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={taxSettings.restaurant_food_tax}
                onChange={(e) => setTaxSettings({ ...taxSettings, restaurant_food_tax: parseFloat(e.target.value) || 0 })}
                placeholder="5"
              />
            </div>
            
            {/* Restaurant Beverages Tax */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <span className="h-4 w-4 text-amber-500">🍺</span>
                Restaurant Beverages Tax (%)
              </Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={taxSettings.restaurant_beverages_tax}
                onChange={(e) => setTaxSettings({ ...taxSettings, restaurant_beverages_tax: parseFloat(e.target.value) || 0 })}
                placeholder="18"
              />
            </div>
            
            {/* Other Services Tax */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Settings className="h-4 w-4 text-gray-500" />
                Other Services Tax (%)
              </Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={taxSettings.other_services_tax}
                onChange={(e) => setTaxSettings({ ...taxSettings, other_services_tax: parseFloat(e.target.value) || 0 })}
                placeholder="18"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button onClick={handleSaveTaxSettings} className="flex-1">
                Save Tax Settings
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setTaxSettingsOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Types Assignment Dialog */}
      <Dialog open={roomTypeDialogOpen} onOpenChange={setRoomTypeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-blue-600" />
              Assign Room Types - {selectedHotelForRoomTypes?.name}
            </DialogTitle>
            <DialogDescription>
              Select which room types should be available for this hotel. Hotel admins will only see the selected room types when creating rooms.
            </DialogDescription>
          </DialogHeader>
          
          {loadingRoomTypes ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto py-4">
              <div className="space-y-3">
                {globalRoomTypes.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No room types available. Ask Super Admin to create room types first.
                  </p>
                ) : (
                  globalRoomTypes.map((roomType) => (
                    <div
                      key={roomType.id}
                      className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedRoomTypes.includes(roomType.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleRoomTypeToggle(roomType.id)}
                    >
                      <Checkbox
                        checked={selectedRoomTypes.includes(roomType.id)}
                        onCheckedChange={() => handleRoomTypeToggle(roomType.id)}
                        className="h-5 w-5"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{roomType.name}</h4>
                        {roomType.description && (
                          <p className="text-sm text-gray-500 mt-1">{roomType.description}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-gray-500">
                {selectedRoomTypes.length} of {globalRoomTypes.length} room types selected
              </span>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setRoomTypeDialogOpen(false)}
                  disabled={savingRoomTypes}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveRoomTypes}
                  disabled={savingRoomTypes}
                  data-testid="save-room-types"
                >
                  {savingRoomTypes ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Room Types'
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hotel Amenities Allocation Dialog */}
      <HotelAmenitiesAllocationDialog
        open={amenitiesDialogOpen}
        onOpenChange={setAmenitiesDialogOpen}
        hotelId={selectedHotelForAmenities?.id}
        hotelName={selectedHotelForAmenities?.name}
      />
    </div>
  );
}
